import {
  ButtonComponentV2,
  Maybe,
  Query,
  SelfFundedItemResultTypenames,
  SelfFundedOfferComponentsResultTypenames,
  TagColor,
  VxlActionTypenames,
} from '@varo/graphql-schema-types';
import { router } from 'expo-router';
import { ActivatedOfferCardProps } from 'features/rewards/components/ActivatedOfferCard/ActivatedOffer';
import {
  Copy,
  OfferCardProps,
  OfferItem,
} from 'features/rewards/components/OfferCard/OfferCard';
import { IconKeyType } from 'shared/components/vxl/icon/Icon/Icon';
import { TagType } from 'shared/components/vxl/Tag/Tag';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import { tagColorMapper } from 'shared/utils/tagColorMapper/tagColorMapper';

export type SelfFundedOfferComponent =
  | ({ type: 'offer-card'; sectionTitle?: Copy } & OfferCardProps)
  | ({
      type: 'activated-offer-card';
      sectionTitle?: Copy;
    } & ActivatedOfferCardProps);

export const formatSelfFundedOfferComponents = (
  data?: Query['selfFundedOfferComponents'],
): SelfFundedOfferComponent[] => {
  const logSelfFundedOfferTileError = (message: unknown) => {
    logErrorWithContext(message, {
      featureArea: FeatureArea.REWARDS,
      responsibleTeam: ResponsibleTeam.CASHBACK_REWARDS,
      severity: ErrorSeverity.MEDIUM,
    });
  };

  const handleTileTapAction = (
    footerButton: Maybe<ButtonComponentV2> | undefined,
  ) => {
    if (
      footerButton?.buttonOnClick?.__typename !==
      VxlActionTypenames.RouteNavigationAction
    ) {
      logSelfFundedOfferTileError(
        'Invalid or missing navigation action for footerButton',
      );
      return;
    }

    const { route } = footerButton.buttonOnClick;
    if (!route) {
      logSelfFundedOfferTileError(
        'Route object is missing in the navigation action',
      );
      return;
    }

    try {
      router.push(route as Parameters<typeof router.push>[0]);
    } catch (error) {
      logSelfFundedOfferTileError(error);
    }
  };

  if (!data) {
    return [];
  }

  if (
    data.__typename ===
    SelfFundedOfferComponentsResultTypenames.SelfFundedOfferComponentsError
  ) {
    return [];
  }

  if (
    data.__typename ===
    SelfFundedOfferComponentsResultTypenames.SelfFundedOfferComponentsSuccess
  ) {
    const { selfFundedItem, sectionName } = data;

    if (!selfFundedItem) {
      return [];
    }

    if (
      selfFundedItem.__typename ===
      SelfFundedItemResultTypenames.PromotionalSelfFundedOffer
    ) {
      const { id, iconUrls, title, tag, footerButton } = selfFundedItem;

      const bodyItems: OfferItem[] =
        iconUrls?.map((url, index) => ({
          id: `${id}_icon_${index}`,
          iconUrl: url,
          title: { text: title || '' } as Copy,
        })) || [];

      const offerCard: SelfFundedOfferComponent = {
        type: 'offer-card',
        id,
        title: { text: title || '' } as Copy,
        bodyItems,
        buttonTitle: footerButton?.buttonText
          ? ({ text: footerButton.buttonText } as Copy)
          : undefined,
        tag: tag
          ? {
              text: tag.text || '',
              type: mapTagColorToTagType(tag.tagColor),
            }
          : undefined,
        sectionTitle: sectionName ? { text: sectionName } : undefined,
        onPress: () => {
          handleTileTapAction(footerButton);
        },
        onActivate: () => {
          handleTileTapAction(footerButton);
        },
        accessibilityLabel: `Promotional offer: ${title}`,
        testID: `promotional-offer-${id}`,
      };

      return [offerCard];
    }

    if (
      selfFundedItem.__typename ===
      SelfFundedItemResultTypenames.ActivatedSelfFundedOffer
    ) {
      const { offerId, avatarUrl, title, subTitle, tag, onClick } =
        selfFundedItem;

      const activatedOfferCard: SelfFundedOfferComponent = {
        type: 'activated-offer-card',
        offerId,
        avatarUrl,
        title: { text: title || '' } as Copy,
        subTitle: { text: subTitle || '' } as Copy,
        sectionTitle: sectionName ? ({ text: sectionName } as Copy) : undefined,
        tag: tag
          ? {
              text: tag.text || '',
              type: mapTagColorToTagType(tag.tagColor),
              iconKey: tag.icon as IconKeyType,
            }
          : undefined,
        onClick: () => {
          if (onClick?.route) {
            try {
              router.push(onClick.route as Parameters<typeof router.push>[0]);
            } catch (error) {
              logErrorWithContext(error, {
                featureArea: FeatureArea.REWARDS,
                responsibleTeam: ResponsibleTeam.CASHBACK_REWARDS,
                severity: ErrorSeverity.MEDIUM,
              });
            }
          }
        },
        accessibilityLabel: `Activated offer: ${title}${subTitle ? ` - ${subTitle}` : ''}`,
        testID: `activated-offer-${offerId}`,
      };

      return [activatedOfferCard];
    }
  }

  return [];
};

const mapTagColorToTagType = (tagColor?: string): TagType => {
  if (tagColor) {
    const normalizedTagColor = tagColor
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('');
    const tagColorEnum = normalizedTagColor as keyof typeof TagColor;
    if (TagColor[tagColorEnum] !== undefined) {
      const result = tagColorMapper(TagColor[tagColorEnum]);
      return result;
    }
  }

  return TagType.INFO;
};
